/**
 * LoginForm Component
 * ===================
 * Stetson-branded authentication form for protected routes.
 * Handles demo credential login with redirect to addi.vaultailab.com.
 */

import React, { useState } from 'react';
import { Eye, EyeOff, Lock, User, ArrowRight } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { AUTH_CONFIG } from '../../utils/constants';

/**
 * LoginForm Component
 * Provides Stetson-branded login form with validation and error handling.
 * On success, redirects to /demo (narrative experience on this site).
 *
 * @returns {JSX.Element} Login form component
 */
export function LoginForm() {
  const { login } = useAuth();
  
  // Form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  /**
   * Handle form submission
   * @param {React.FormEvent} e - Form event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset error
    setError('');
    
    // Validate inputs
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }
    
    setIsLoading(true);
    
    // Attempt login
    const success = login(username, password);
    
    if (success) {
      // Redirect to /demo (narrative experience) on this site; use location so main app is not affected
      window.location.href = AUTH_CONFIG.DEMO_PATH;
      return;
    } else {
      setError('Invalid username or password');
      setIsLoading(false);
    }
  };
  
  /**
   * Fill demo credentials
   */
  const fillDemoCredentials = () => {
    setUsername(AUTH_CONFIG.DEMO_USERNAME);
    setPassword(AUTH_CONFIG.DEMO_PASSWORD);
    setError('');
  };
  
  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-2xl shadow-2xl p-8">
        {/* Header - Stetson Branded */}
        <div className="text-center mb-8">
          {/* Stetson Logo Placeholder */}
          <div className="w-20 h-20 bg-gradient-to-br from-dme-green to-dme-green-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-white font-bold text-2xl">S</span>
          </div>
          <h2 className="text-2xl font-bold text-dme-blue-800 mb-2">
            Stetson University
          </h2>
          <p className="text-dme-green-600 font-medium mb-1">
            Commitment Predictor Hub
          </p>
          <p className="text-dme-gray-500 text-sm">
            Sign in to access the ADDI demo and dashboard
          </p>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">!</span>
            </div>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Username Field */}
          <div>
            <label 
              htmlFor="username" 
              className="block text-sm font-medium text-dme-gray-700 mb-2"
            >
              Username
            </label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-dme-gray-400" />
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className="w-full pl-12 pr-4 py-3 border border-dme-gray-200 rounded-lg focus:ring-2 focus:ring-dme-green focus:border-transparent transition-all outline-none"
                disabled={isLoading}
                autoComplete="username"
              />
            </div>
          </div>
          
          {/* Password Field */}
          <div>
            <label 
              htmlFor="password" 
              className="block text-sm font-medium text-dme-gray-700 mb-2"
            >
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-dme-gray-400" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full pl-12 pr-12 py-3 border border-dme-gray-200 rounded-lg focus:ring-2 focus:ring-dme-green focus:border-transparent transition-all outline-none"
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-dme-gray-400 hover:text-dme-gray-600 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          
          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-6 bg-gradient-to-r from-dme-green to-dme-green-600 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-dme-green/30 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <span>Sign In</span>
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>
        
        {/* Demo Credentials Hint */}
        <div className="mt-6 pt-6 border-t border-dme-gray-100">
          <p className="text-center text-sm text-dme-gray-500 mb-3">
            For demo purposes, use these credentials:
          </p>
          <button
            type="button"
            onClick={fillDemoCredentials}
            className="w-full py-2 px-4 bg-dme-green-50 text-dme-green-700 font-medium rounded-lg hover:bg-dme-green-100 transition-colors text-sm"
          >
            Use Demo Credentials
          </button>
          <div className="mt-3 text-center text-xs text-dme-gray-400 font-mono">
            Username: {AUTH_CONFIG.DEMO_USERNAME} | Password: {AUTH_CONFIG.DEMO_PASSWORD}
          </div>
        </div>

        {/* Domain Note */}
        <div className="mt-4 p-3 bg-dme-blue-50 rounded-lg">
          <p className="text-xs text-dme-blue-600 text-center">
            After sign-in you’ll go to the <strong>Demo</strong>; dashboard: <strong>addi.vaultailab.com</strong>
          </p>
        </div>
      </div>
      
      {/* Back to Home */}
      <div className="mt-6 text-center">
        <a 
          href="/"
          className="text-sm text-white/80 hover:text-dme-green-300 transition-colors"
        >
          ← Back to DME-CPH Home
        </a>
      </div>
    </div>
  );
}

export default LoginForm;
